import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  InputBase,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import VideoCard from "../../components/VideoCard";
import NativeVideoPicker from "../../components/NativeVideoPicker";
import api from "../../../../conf/api";

/**
 * Step 3 — search WooGlobe by WGID, or pick a CreatorVault-native video
 * (Video Library upload, approved and past its async Dropbox pipeline), and
 * pick video(s) to push. Compilations (WCA ids) are still out of scope here
 * (unchanged) — the backend's WCA-rejection guard (admin_portal_push_views.py)
 * is untouched.
 *
 * A native video pushed from here is accepted on CreatorVault's side, but
 * Admin Portal's own downstream WGID validation (resolve_video_by_wgid, on
 * their side) may still reject it at actual push time since a WGC id isn't a
 * real WooGlobe WGID — that's an accepted external limitation until Admin
 * Portal's own backend is updated separately. A rejection surfaces as a
 * "Failed" status + error message on the push row in the Approvals tab, same
 * as any other push failure — see ApprovalsPanel.js.
 */
export default function Step3VideoSelection({ selectedVideos, onVideosChange, groupFeeds, selectedFeedIds }) {
  const [sourceTab, setSourceTab] = useState(0); // 0 = search WooGlobe, 1 = use native video
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiVideoCheck, setAiVideoCheck] = useState(false);
  const searchRef = useRef(null);

  // The native-video tab is entirely NativeVideoPicker's business now
  // (search/filter/fetch + the top-10 cap) — it is the same component Brand
  // Publish's Step 2 renders, so both flows stay in sync.
  const handleSourceTabChange = (_, tab) => setSourceTab(tab);

  // Whether each selected video has already been pushed (or is pending/
  // approved) to Admin Portal — { [unique_key]: [AdminPortalMrssPush, ...] }
  const [pushStatus, setPushStatus] = useState({});

  useEffect(() => {
    const keys = selectedVideos.map((v) => v.unique_key).filter(Boolean);
    const missing = keys.filter((k) => !(k in pushStatus));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/mrss/admin-portal/push-status/", {
          params: { keys: missing.join(",") },
        });
        const results = res.data?.results || {};
        if (cancelled) return;
        setPushStatus((prev) => {
          const next = { ...prev };
          missing.forEach((k) => {
            next[k] = results[k] || [];
          });
          return next;
        });
      } catch {
        if (cancelled) return;
        setPushStatus((prev) => {
          const next = { ...prev };
          missing.forEach((k) => {
            if (!(k in next)) next[k] = [];
          });
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideos]);

  // LIVE duplicate check against the feed(s) picked in Step 1, straight from
  // Admin Portal's own DB (not this app's push records) — { [unique_key]:
  // [{feedId, feedTitle, state}] }, "state" being "published" or "enqueued".
  // This is what catches a video already pushed/published through Admin
  // Portal's own UI, which pushStatus above can never see (no local row) and
  // would otherwise only surface as a 409 once execute_push() tries it for
  // real — see AdminPortalFeedDuplicateStatusView's docstring.
  const [adminPortalDuplicates, setAdminPortalDuplicates] = useState({});

  useEffect(() => {
    const keys = selectedVideos.map((v) => v.unique_key).filter(Boolean);
    const feeds = (groupFeeds || []).filter((f) => (selectedFeedIds || []).includes(f.id));
    if (keys.length === 0 || feeds.length === 0) {
      setAdminPortalDuplicates({});
      return;
    }

    let cancelled = false;
    (async () => {
      const perFeed = await Promise.all(
        feeds.map(async (feed) => {
          try {
            const res = await api.get(`/mrss/admin-portal/feeds/${feed.id}/duplicate-status/`, {
              params: { wgids: keys.join(",") },
            });
            return { feed, results: res.data?.results || {} };
          } catch {
            return { feed, results: {} };
          }
        })
      );
      if (cancelled) return;
      const next = {};
      perFeed.forEach(({ feed, results }) => {
        Object.entries(results).forEach(([wgid, state]) => {
          if (state !== "published" && state !== "enqueued") return;
          if (!next[wgid]) next[wgid] = [];
          next[wgid].push({ feedId: feed.id, feedTitle: feed.title || feed.name || `Feed #${feed.id}`, state });
        });
      });
      setAdminPortalDuplicates(next);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideos, groupFeeds, selectedFeedIds]);

  const fetchVideos = async (searchText) => {
    if (!searchText?.trim()) return;
    setLoading(true);
    try {
      const keys = searchText.split(",").map((k) => k.trim()).filter(Boolean);
      const requests = keys.map((key) => {
        const params = { search: key, limit: 20, orderType: "DES", page: 1 };
        if (aiVideoCheck) {
          params.category_27 = "true";
          params.ai_based = "true";
        }
        return axios.get(process.env.REACT_APP_WEBSITE + "/apis/videos-filter", { params });
      });
      const responses = await Promise.all(requests);
      const all = responses.flatMap((r) => r.data);
      const unique = Array.from(new Map(all.map((v) => [v.unique_key, v])).values());
      setSearchResults(unique);
    } catch {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const addVideo = (video) => {
    if (!selectedVideos.find((v) => v.unique_key === video.unique_key)) {
      onVideosChange([...selectedVideos, video]);
    }
  };

  const removeVideo = (videoKey) => {
    onVideosChange(selectedVideos.filter((v) => v.unique_key !== videoKey));
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 3 — Select Video(s)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Search WooGlobe by title or WGID, or pick an approved CreatorVault-native video.
      </Typography>

      <Tabs value={sourceTab} onChange={handleSourceTabChange} sx={{ mb: 2, minHeight: 36 }}>
        <Tab label="Search WooGlobe" sx={{ minHeight: 36 }} />
        <Tab label="Use Native Video" sx={{ minHeight: 36 }} />
      </Tabs>

      {sourceTab === 0 && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Paper variant="outlined" sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <InputBase
                inputRef={searchRef}
                placeholder="Search by title or ID…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchVideos(searchRef.current?.value);
                }}
                sx={{ flex: 1, px: 2 }}
              />
              <IconButton
                onClick={() => {
                  if (searchRef.current) searchRef.current.value = "";
                  setSearchResults([]);
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => fetchVideos(searchRef.current?.value)}>
                <SearchIcon />
              </IconButton>
            </Paper>
            <Tooltip title="Enable to search AI-generated videos only" arrow>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={aiVideoCheck}
                    onChange={(e) => setAiVideoCheck(e.target.checked)}
                    color="primary"
                  />
                }
                label="AI Videos"
                sx={{ mr: 0, whiteSpace: "nowrap" }}
              />
            </Tooltip>
          </Box>

          {loading && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 1 }} />}

          {searchResults.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" color="text.secondary">
                Click a result to add it:
              </Typography>
              <Grid container spacing={1} mt={0.5}>
                {searchResults.map((video) => {
                  const already = !!selectedVideos.find((v) => v.unique_key === video.unique_key);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={video.unique_key}>
                      <Box
                        onClick={() => !already && addVideo(video)}
                        sx={{
                          cursor: already ? "default" : "pointer",
                          opacity: already ? 0.45 : 1,
                          border: "2px solid",
                          borderColor: already ? "success.main" : "transparent",
                          borderRadius: 1,
                        }}
                      >
                        <VideoCard video={video} />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </>
      )}

      {sourceTab === 1 && (
        <Box mb={2}>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Accepted on CreatorVault's side, but Admin Portal's own WGID validation may still
            reject a native video at push time until their backend is updated separately — a
            rejection shows up as "Failed" with a reason in the Approvals tab, same as any other
            push failure.
          </Alert>
          <NativeVideoPicker
            selectedKeys={selectedVideos.map((v) => v.unique_key)}
            onSelect={addVideo}
          />
        </Box>
      )}

      {selectedVideos.length === 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          No videos selected yet — search above to add some.
        </Alert>
      )}

      {selectedVideos.map((video) => {
        const priorPushes = pushStatus[video.unique_key] || [];
        const hasPrior = priorPushes.length > 0;
        const adminPortalDupes = adminPortalDuplicates[video.unique_key] || [];
        const hasAdminPortalDupe = adminPortalDupes.length > 0;
        return (
          <Paper
            key={video.unique_key}
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 1,
              borderColor: hasAdminPortalDupe ? "error.main" : hasPrior ? "warning.main" : "divider",
              bgcolor: hasAdminPortalDupe
                ? "rgba(239,68,68,0.06)"
                : hasPrior
                ? "rgba(245,158,11,0.06)"
                : "background.paper",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {video.title || video.video_title || video.unique_key}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {video.unique_key}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                {video.is_native_video ? (
                  <Chip size="small" color="secondary" variant="outlined" label="Native Video" />
                ) : (
                  <Chip size="small" color="primary" variant="outlined" label="WGID" />
                )}
                {hasAdminPortalDupe && (
                  <Tooltip
                    title={adminPortalDupes
                      .map(
                        (d) =>
                          `${video.unique_key} is already ${d.state === "published" ? "published in" : "queued for"} “${d.feedTitle}” in Admin Portal.`
                      )
                      .join(" ")}
                  >
                    <Chip
                      size="small"
                      color="error"
                      variant="filled"
                      label={`Already in Admin Portal · ${adminPortalDupes.length} feed${adminPortalDupes.length !== 1 ? "s" : ""}`}
                    />
                  </Tooltip>
                )}
                {hasPrior && (
                  <Tooltip
                    title={priorPushes
                      .map(
                        (p) =>
                          `${p.admin_portal_feed_title || p.admin_portal_feed_id}: ${p.approval_status}/${p.status}` +
                          (p.created_date ? ` — created ${new Date(p.created_date).toLocaleString()}` : "")
                      )
                      .join(", ")}
                  >
                    <Chip
                      size="small"
                      color="warning"
                      variant="filled"
                      label={`Pushed · ${priorPushes.length} target${priorPushes.length !== 1 ? "s" : ""}`}
                    />
                  </Tooltip>
                )}
                <IconButton size="small" color="error" onClick={() => removeVideo(video.unique_key)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
